/**
 * Поиск функциональной зоны по ID в слое линейных границ
 * Ищет в слое martin.functional_areas_mvt (линейные границы зон)
 *
 * Ищет по полю: id
 *
 * @param {Object} map - Mapbox GL JS map instance
 * @param {string} zoneId - ID зоны для поиска
 * @returns {Object|null} { id, type, lat, lon, feature } или null если не найдена
 */
export function searchFunctionalAreaInTiles (map, zoneId) {
    if (!map || !zoneId) {
        return null;
    }

    const sourceId = 'Функциональные зоны_source';
    const sourceLayer = 'martin.functional_areas_mvt';

    try {
        // Проверяем наличие источника на карте
        const source = map.getSource(sourceId);
        if (!source) {
            return null;
        }

        // Получаем features из источника по фильтру ID
        const features = map.querySourceFeatures(sourceId, {
            sourceLayer,
            filter: [
                '==',
                [ 'to-string', [ 'get', 'id' ] ],
                String(zoneId),
            ],
        });

        if (features.length === 0) {
            return null;
        }

        // Берём первый найденный feature (линия)
        const feature = features[0];
        const props = feature.properties;
        const geom = feature.geometry;

        // Вычисляем центр линии (среднее значение координат)
        let lat = null;
        let lon = null;

        if (geom) {
            if (geom.type === 'LineString' && geom.coordinates && geom.coordinates.length > 0) {
                // Берём середину линии
                const midIndex = Math.floor(geom.coordinates.length / 2);
                [ lon, lat ] = geom.coordinates[midIndex];
            } else if (geom.type === 'Polygon' && geom.coordinates && geom.coordinates.length > 0) {
                // На случай если это будет полигон
                const midIndex = Math.floor(geom.coordinates[0].length / 2);
                [ lon, lat ] = geom.coordinates[0][midIndex];
            } else if (geom.type === 'Point' && geom.coordinates) {
                [ lon, lat ] = geom.coordinates;
            }
        }

        return {
            id: props.id || zoneId,
            type: props.type || null,
            lat,
            lon,
            feature,
            featureId: props.id,
        };
    } catch (err) {
        return null;
    }
}

/**
 * Получить все функциональные зоны из линейного слоя
 *
 * @param {Object} map - Mapbox GL JS map instance
 * @returns {Array} Массив всех зон с основной информацией
 */
export function getAllFunctionalAreasFromTiles (map) {
    if (!map) return [];

    const sourceId = 'Функциональные зоны_source';
    const sourceLayer = 'martin.functional_areas_mvt';

    try {
        const source = map.getSource(sourceId);
        if (!source) return [];

        const features = map.querySourceFeatures(sourceId, {
            sourceLayer,
        });

        return features.map(f => ({
            id: f.properties.id,
            type: f.properties.type,
        }));
    } catch (err) {
        return [];
    }
}

/**
 * Применить визуальную подсветку к найденной зоне (контур)
 * Меняет цвет и толщину линии контура найденной зоны
 *
 * @param {Object} map - Mapbox GL JS map instance
 * @param {Object} result - Результат поиска { id, lat, lon, type }
 */
export function highlightFunctionalArea (map, result) {
    if (!map || !result || !result.id) {
        return;
    }

    try {
        const layerId = 'Функциональные зоны';
        const zoneId = result.id;

        // Подсветка через dynamic paint properties для контура
        // Увеличиваем толщину для найденной зоны, оставляем нормальную для остальных
        map.setPaintProperty(layerId, 'line-width', [
            'case',
            [ '==', [ 'get', 'id' ], zoneId ],
            6,              // Толще для найденной
            3,              // Нормальная для остальных
        ]);

        // Изменяем opacity для выделения
        map.setPaintProperty(layerId, 'line-opacity', [
            'case',
            [ '==', [ 'get', 'id' ], zoneId ],
            1,              // Полная видимость
            0.7,            // Немного приглушаем остальные
        ]);

        // Делаем пан с зумом к найденной зоне
        if (result.lat && result.lon) {
            map.flyTo({
                center: [ result.lon, result.lat ],
                zoom: 12,
                duration: 1500,
                padding: {
                    top: 100,
                    bottom: 100,
                    left: 100,
                    right: 100,
                },
            });
        }
    } catch (err) {
        // Silently fail
    }
}

/**
 * Очистить подсветку и восстановить оригинальный вид слоя
 *
 * @param {Object} map - Mapbox GL JS map instance
 */
export function clearFunctionalAreaHighlight (map) {
    if (!map) return;

    try {
        const layerId = 'Функциональные зоны';

        // Восстанавливаем оригинальные свойства линии
        map.setPaintProperty(layerId, 'line-width', 3);
        map.setPaintProperty(layerId, 'line-opacity', 1);
    } catch (err) {
        // Silently fail
    }
}
